import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ErrorNotice, HexPattern, Icon, PoweredBy, Toggle, Wordmark } from '@hive/ui';
import { useAuth } from '../auth/AuthContext';
import { session } from '../auth/session';

const VERSION = 'Keeper 1.4';

function Fields({
  username,
  password,
  remember,
  invalid,
  onUsername,
  onPassword,
  onRemember,
}: {
  username: string;
  password: string;
  remember: boolean;
  invalid: boolean;
  onUsername(v: string): void;
  onPassword(v: string): void;
  onRemember(v: boolean): void;
}) {
  return (
    <div className="k-login__fields">
      <div className="hv-field">
        <label className="hv-field__label" htmlFor="username">
          Username
        </label>
        <input
          id="username"
          className="hv-input"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={username}
          aria-invalid={invalid || undefined}
          onChange={(e) => onUsername(e.target.value)}
          required
        />
      </div>
      <div className="hv-field">
        <label className="hv-field__label" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          className="hv-input"
          autoComplete="current-password"
          value={password}
          aria-invalid={invalid || undefined}
          onChange={(e) => onPassword(e.target.value)}
          required
        />
      </div>
      <Toggle label="Remember this device" checked={remember} onChange={onRemember} />
    </div>
  );
}

export function LoginPage() {
  const { signIn, signedIn } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [result, setResult] = useState<'invalid' | 'unreachable' | null>(null);
  const [busy, setBusy] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/';
  if (signedIn) return <Navigate to={from} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await signIn(username, password, remember);
    setBusy(false);
    setResult(r === 'ok' ? null : r);
  }

  const lastVenue = session.lastVenue();
  const pairLink = (
    <Link to="/pair" className="hv-btn hv-btn--link k-login__pair">
      <Icon name="pair" size={18} />
      Pair a new TV screen
    </Link>
  );

  // Error state: its own design (HF-LoginError), not an improvised banner.
  if (result) {
    return (
      <main className="hv-root k-login k-login--error">
        <HexPattern />
        <form className="k-login__card k-login__card--error" onSubmit={submit}>
          <div className="k-login__brandrow">
            <Wordmark />
            <span className="hv-wordmark__product">Keeper</span>
          </div>
          <div className="k-login__intro">
            <h1 className="k-login__title">Sign in to your venue</h1>
            <p className="hv-muted">Use the username and staff password issued by your venue manager.</p>
          </div>
          {result === 'invalid' ? (
            <ErrorNotice title="That username and password don't match">Check both and try again. Passwords are case sensitive.</ErrorNotice>
          ) : (
            <ErrorNotice title="Keeper can't reach the venue server">Check the tablet's connection, then try again.</ErrorNotice>
          )}
          <Fields
            username={username}
            password={password}
            remember={remember}
            invalid={result === 'invalid'}
            onUsername={setUsername}
            onPassword={setPassword}
            onRemember={setRemember}
          />
          <div className="k-login__actions">
            <button type="submit" className="hv-btn hv-btn--primary hv-btn--lg hv-btn--block" disabled={busy}>
              {busy ? 'Signing in…' : 'Try again'}
            </button>
            {pairLink}
            <span className="k-login__help">Forgotten your password? Ask your venue manager</span>
          </div>
        </form>
        <div className="k-login__footer">
          <PoweredBy suffix={VERSION} />
        </div>
      </main>
    );
  }

  return (
    <main className="hv-root k-login">
      <aside className="k-login__aside hv-on-dark">
        <HexPattern ink="var(--bm-white)" opacity={0.09} corner="bottom-left" id="aside-hx" />
        <Wordmark tone="white" product="Keeper" size={30} />
        <div className="k-login__pitch">
          <h2 className="k-login__headline">Licensed live sport, on every screen in the house.</h2>
          <div className="k-login__facts">
            {lastVenue && (
              <span>
                <span className="hv-dot hv-dot--healthy hv-dot--glow" />
                {lastVenue} · last signed in on this device
              </span>
            )}
            <span>
              <span className="hv-dot k-dot-arctic" />
              Change any screen in about two seconds
            </span>
          </div>
        </div>
        <span className="k-login__powered">
          <PoweredBy suffix={VERSION} />
        </span>
      </aside>
      <section className="k-login__main">
        <HexPattern id="main-hx" />
        <form className="k-login__card" onSubmit={submit}>
          <div className="k-login__intro">
            <h1 className="k-login__title">Sign in to your venue</h1>
            <p className="hv-muted">Use the username and staff password issued by your venue manager.</p>
          </div>
          <Fields
            username={username}
            password={password}
            remember={remember}
            invalid={false}
            onUsername={setUsername}
            onPassword={setPassword}
            onRemember={setRemember}
          />
          <div className="k-login__actions">
            <button type="submit" className="hv-btn hv-btn--primary hv-btn--lg hv-btn--block" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            {pairLink}
            <span className="k-login__help">Need help? Contact venue support</span>
          </div>
        </form>
      </section>
    </main>
  );
}
